import { SmartLink } from "@selector/shared/components";
import { type FC, useRef } from "react";
import {
  DialogComponent,
  Popover,
  TriggerButton,
  TriggerButtonSize,
  VStack,
} from "widgets";
import { useOverlay } from "widgets/hooks";

import { type ProjectFile } from "@/graphql.ts";
import { FileTile } from "@/modules/files/components/FileTile.js";
import { nonBreakingSpace } from "@/settings/settings.js";

import { type IAgGridCellProps } from "../../cell-base-interfaces.js";
import { traverseAndCollect } from "../../column-props-factory.js";
import ListCellContent from "./ListCellContent.js";
import { TableCell, type TableCellProps } from "./TableCell.js";

const FileCell: FC<IAgGridCellProps & Pick<TableCellProps, "triggerButtonProps">> = (props) => {
  const { data, colDef } = props;

  const value =
    colDef.groupField != undefined || colDef.field != undefined
      ? traverseAndCollect(data, (colDef.groupField || colDef.field)!)
      : data;

  const multiValues: ProjectFile[] = Array.isArray(value) ? value : [value];
  const projectFiles = multiValues.filter((file): file is ProjectFile => file != null);

  const files = projectFiles.map((file) => ({
    name: file.fileName ?? "Unnamed file",
    link: !file.hidden && !file.deleted && file.id ? `/content/files?file=${file.id}` : undefined,
  }));
  const items = files.map(({ name, link }) => ({
    value: link ? (
      <SmartLink to={link} style={{ width: "auto", display: "inline" }}>
        {name}
      </SmartLink>
    ) : (
      name
    ),
    tooltip: name,
  }));

  if (data == null) {
    return null;
  }

  return (
    <TableCell
      triggerButtonProps={props.triggerButtonProps}
      alternativeButton={projectFiles.length ? <FilesPopoverButton projectFiles={projectFiles} /> : undefined}
    >
      {files.length ? (
        <ListCellContent
          items={items}
          colDef={props.colDef}
          context={props.context}
          style={{ width: "100%", minWidth: 0 }}
        />
      ) : (
        nonBreakingSpace
      )}
    </TableCell>
  );
};

const FilesPopoverButton: FC<{ projectFiles: ProjectFile[] }> = (props) => {
  const projectFiles = props.projectFiles.filter((projectFile) => projectFile);
  const { state, overlayProps, triggerProps } = useOverlay();
  const triggerRef = useRef<HTMLButtonElement>(null);

  if (!projectFiles?.length) {
    return null;
  }

  return (
    <>
      <TriggerButton
        label=""
        icon="more_vert"
        size={TriggerButtonSize.Small}
        {...triggerProps}
        ref={triggerRef}
      />
      <Popover state={state} triggerRef={triggerRef} placement="top" offset={8} {...overlayProps}>
        <DialogComponent closeFn={() => state.close()}>
          <VStack>
            {projectFiles.map((file, i) => (
              <FileTile key={file.id ?? i} file={file} />
            ))}
          </VStack>
        </DialogComponent>
      </Popover>
    </>
  );
};

export default FileCell;
